/**
 * Client-side mirror of the backend PasswordPolicy (min 10 chars, ≥1 letter, ≥1 digit) plus a
 * strength score for live feedback. `valid` reflects the hard rules the server enforces; `score`
 * (0–4) and `label` are advisory. The server remains the source of truth (it also rejects common
 * leaked passwords, which we don't replicate client-side).
 */
export interface PasswordStrength {
  meetsMin: boolean
  hasLetter: boolean
  hasDigit: boolean
  valid: boolean
  score: number // 0..4
  label: string
}

export function passwordStrength(pw: string): PasswordStrength {
  const meetsMin = pw.length >= 10
  const hasLetter = /[a-zA-Z]/.test(pw)
  const hasDigit = /\d/.test(pw)
  const valid = meetsMin && hasLetter && hasDigit

  let score = 0
  if (pw.length >= 10) score++
  if (pw.length >= 14) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/[^a-zA-Z0-9]/.test(pw)) score++
  score = Math.min(score, 4)

  const label = valid
    ? score >= 4
      ? 'Strong'
      : score === 3
        ? 'Good'
        : 'Fair'
    : 'Keep going'

  return { meetsMin, hasLetter, hasDigit, valid, score, label }
}
